<script setup>
import { computed, onMounted, ref } from 'vue';
import AdminHeader from '../AdminHeader.vue';
import EvalAgentsTable from '@/components/table/Table.vue';
import EvalAgentFormModal from '@/components/modals/EvalAgentFormModal.vue';
import { useToast } from '@/composables/use-toast';
import { getEvalAgents, toggleEvalAgentActive } from '@/services/evalAgentService';

const { addToast } = useToast();

const isLoading = ref(false);
const evalAgents = ref([]);
const isModalOpen = ref(false);
const selectedAgentId = ref(null);

const searchQuery = ref('');
const currentPage = ref(1);
const itemsPerPage = ref(10);
const sortBy = ref('updated_at');
const sortOrder = ref('desc');

const columns = ref([
  { label: 'Name', key: 'name', type: 'text', sortable: true, truncate: 45 },
  { label: 'Env', key: 'harbor_env', type: 'text', sortable: true, truncate: 18 },
  { label: 'Agent', key: 'harbor_agent', type: 'text', sortable: true, truncate: 30 },
  { label: 'Model', key: 'harbor_model', type: 'text', sortable: true, truncate: 40 },
  { label: 'Status', key: 'active', sortable: true },
  { label: 'Advanced', key: 'advanced', sortable: false },
  { label: 'Updated At', key: 'updated_at', type: 'date', sortable: true },
  { label: 'Actions', key: 'actions' },
]);

const parseAgentsList = (response) => {
  const data = response?.data;
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.agents)) return data.agents;
  return [];
};

const handleSort = (column) => {
  if (sortBy.value === column) {
    sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc';
  } else {
    sortBy.value = column;
    sortOrder.value = 'asc';
  }
};

const jsonPreview = (value) => {
  if (!value) return '';
  try {
    const raw = typeof value === 'string' ? value : JSON.stringify(value);
    return raw.length > 80 ? `${raw.slice(0, 80)}...` : raw;
  } catch {
    return String(value);
  }
};

const normalizeHarborModel = (value) => {
  const trimmed = String(value ?? '').trim();
  if (!trimmed) return '';
  return trimmed.toLowerCase().startsWith('openrouter/')
    ? trimmed.slice('openrouter/'.length)
    : trimmed;
};

const filteredAndSortedAgents = computed(() => {
  let result = [...evalAgents.value];

  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase();
    result = result.filter((agent) => {
      const haystacks = [
        agent.name,
        agent.harbor_env,
        agent.harbor_agent,
        agent.harbor_model,
        normalizeHarborModel(agent.harbor_model),
        agent.created_at,
        agent.updated_at,
        jsonPreview(agent.agent_kwargs),
        jsonPreview(agent.harbor_args),
      ];
      return haystacks.some((value) => String(value ?? '').toLowerCase().includes(query));
    });
  }

  if (sortBy.value) {
    result.sort((a, b) => {
      let aValue = a[sortBy.value];
      let bValue = b[sortBy.value];

      if (['created_at', 'updated_at'].includes(sortBy.value)) {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
      }
      if (sortBy.value === 'active') {
        aValue = Number(aValue);
        bValue = Number(bValue);
      }
      const multiplier = sortOrder.value === 'asc' ? 1 : -1;

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return aValue.localeCompare(bValue, undefined, { sensitivity: 'base' }) * multiplier;
      }
      if (aValue < bValue) return -1 * multiplier;
      if (aValue > bValue) return 1 * multiplier;
      return 0;
    });
  }

  return result;
});

const loadEvalAgentsList = async () => {
  isLoading.value = true;
  try {
    const response = await getEvalAgents();
    evalAgents.value = parseAgentsList(response);
  } catch (error) {
    addToast(error.response?.data?.message || 'Failed to load agent profiles', 'error');
  } finally {
    isLoading.value = false;
  }
};

const handleCreate = () => {
  selectedAgentId.value = null;
  isModalOpen.value = true;
};

const handleEdit = (agent) => {
  selectedAgentId.value = agent.id;
  isModalOpen.value = true;
};

const handleModalClose = () => {
  isModalOpen.value = false;
  selectedAgentId.value = null;
};

const handleModalSaved = () => {
  loadEvalAgentsList();
};

const handleModalDeleted = () => {
  loadEvalAgentsList();
};

const handleToggleActive = async (agent) => {
  try {
    const response = await toggleEvalAgentActive(agent.id, agent.active);
    if (response.status === 200) {
      agent.active = !agent.active;
      addToast('Agent profile updated successfully', 'success');
    } else {
      throw new Error(response.data?.message || 'Failed to update status');
    }
  } catch (error) {
    addToast(error.response?.data?.message || error.message || 'Failed to update status', 'error');
  }
};

onMounted(() => {
  loadEvalAgentsList();
});
</script>

<template>
  <article class="p-5">
    <header class="bg-echoes-background-black">
      <AdminHeader>
        <template #title>
          Eval Agents
        </template>
        <template #description>
          Manage Harbor agent profiles used for agentic leaderboard runs
        </template>
        <template #actions>
          <button
            type="button"
            @click="handleCreate"
            class="inline-flex items-center justify-center px-4 py-2.5 text-base font-medium rounded-xl bg-echoes-primary text-white hover:bg-echoes-primary/80 transition-colors duration-200"
          >
            <i class="mdi mdi-plus mr-2" aria-hidden="true"></i>
            Create Eval Agent
          </button>
        </template>
      </AdminHeader>
    </header>

    <main class="bg-echoes-background-black mb-8">
      <div v-if="isLoading" class="flex justify-center items-center py-8">
        <span class="sr-only">Loading Eval Agents...</span>
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-echoes-primary"></div>
      </div>

      <section v-else aria-label="Eval Agents List">
        <EvalAgentsTable
          :data="filteredAndSortedAgents"
          :columns="columns"
          :empty-message="'No Eval Agents found'"
          :total-items="filteredAndSortedAgents.length"
          :sort-by="sortBy"
          :sort-order="sortOrder"
          @sort="handleSort"
          :search-query="searchQuery"
          @update:search-query="searchQuery = $event"
          :current-page="currentPage"
          @update:current-page="currentPage = $event"
          :items-per-page="itemsPerPage"
          @update:items-per-page="itemsPerPage = $event"
        >
          <template #column-harbor_env="{ item }">
            <span class="inline-flex items-center px-2 py-1 text-xs font-medium rounded border"
              :class="item.harbor_env ? 'bg-blue-500/20 text-blue-300 border-blue-500/30' : 'bg-gray-600/20 text-gray-400 border-gray-600/30'">
              {{ item.harbor_env || 'default' }}
            </span>
          </template>

          <template #column-harbor_model="{ item }">
            {{ normalizeHarborModel(item.harbor_model) || 'N/A' }}
          </template>

          <template #column-active="{ item }">
            <div class="flex items-center gap-3">
              <button
                type="button"
                @click="handleToggleActive(item)"
                :class="[
                  'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out',
                  item.active ? 'bg-green-600' : 'bg-gray-600'
                ]"
                :title="item.active ? 'Click to deactivate' : 'Click to activate'"
              >
                <span
                  aria-hidden="true"
                  :class="[
                    'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
                    item.active ? 'translate-x-5' : 'translate-x-0'
                  ]"
                />
              </button>
              <span :class="item.active ? 'text-green-400 text-xs font-medium' : 'text-gray-400 text-xs font-medium'">
                {{ item.active ? 'Active' : 'Inactive' }}
              </span>
            </div>
          </template>

          <template #column-advanced="{ item }">
            <div class="flex items-center gap-2 flex-wrap">
              <span
                v-if="item.agent_kwargs"
                class="inline-flex items-center px-2 py-1 text-xs font-medium rounded bg-green-600/20 text-green-400 border border-green-600/30"
                :title="jsonPreview(item.agent_kwargs)"
              >
                <i class="mdi mdi-code-json mr-1 text-xs"></i>
                agent_kwargs
              </span>
              <span
                v-if="item.harbor_args"
                class="inline-flex items-center px-2 py-1 text-xs font-medium rounded bg-purple-600/20 text-purple-300 border border-purple-600/30"
                :title="jsonPreview(item.harbor_args)"
              >
                <i class="mdi mdi-console-line mr-1 text-xs"></i>
                harbor_args
              </span>
              <span
                v-if="!item.agent_kwargs && !item.harbor_args"
                class="inline-flex items-center px-2 py-1 text-xs font-medium rounded bg-gray-600/20 text-gray-400 border border-gray-600/30"
              >
                None
              </span>
            </div>
          </template>

          <template #column-actions="{ item }">
            <button
              type="button"
              @click="handleEdit(item)"
              class="inline-flex items-center px-3 py-2 text-sm font-medium rounded-xl bg-echoes-background-black/60 text-white hover:bg-white/5 transition-colors duration-200 border border-echoes-border-light"
              title="Edit Agent Profile"
            >
              <i class="mdi mdi-pencil mr-1"></i>
              Edit
            </button>
          </template>
        </EvalAgentsTable>
      </section>
    </main>

    <EvalAgentFormModal
      :is-open="isModalOpen"
      :agent-id="selectedAgentId"
      @close="handleModalClose"
      @saved="handleModalSaved"
      @delete="handleModalDeleted"
    />
  </article>
</template>
